interface LiveJsonPayloadProps {
  formData: Record<string, any>;
  workflowPath: string;
  outputKey: string;
}

export const LiveJsonPayload = ({ formData, workflowPath, outputKey }: LiveJsonPayloadProps) => {
  // Filter out empty values for cleaner display
  const filteredData: Record<string, any> = {};
  Object.entries(formData).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      filteredData[key] = value;
    }
  });

  const fieldCount = Object.keys(filteredData).length;

  return (
    <div className="mb-6 p-4 bg-brand/10 border border-brand/30 rounded-lg">
      <h5 className="text-brand font-semibold mb-3 flex items-center space-x-2">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
        <span>Live JSON Payload</span>
        <span className="text-xs bg-brand/20 text-brand px-2 py-0.5 rounded">
          {fieldCount} field{fieldCount !== 1 ? 's' : ''}
        </span>
      </h5>
      
      <div className="space-y-3">
        <p className="text-th-secondary text-sm">
          This JSON will be sent to FAL API: <code className="text-brand font-mono text-xs">{workflowPath}</code>
        </p>
        
        {/* Expected Output Path Display */}
        <div className="bg-accent/10 border border-accent/30 rounded-lg p-3">
          <h6 className="text-accent font-medium text-sm mb-2 flex items-center space-x-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13 3l3.09 6.26L22 10.27l-5 4.87 1.18 6.88L12 18.77l-6.18 3.25L7 15.14 2 10.27l6.91-1.01L13 3z"/>
            </svg>
            <span>Expected Output Path</span>
          </h6>
          <div className="space-y-3">
            <div className="space-y-1">
              <p className="text-th-secondary">JavaScript/React access path:</p>
              <code className="text-accent font-mono bg-accent/20 px-2 py-1 rounded block">
                falResult.{outputKey}
              </code>
            </div>
            <div className="bg-status-warning/10 border border-status-warning/20 rounded p-2">
              <p className="text-status-warning text-xs font-medium">💡 FAL Streaming Result Structure</p>
              <p className="text-th-secondary text-xs mt-1">
                FAL streaming returns the final result as: <code className="text-status-warning font-mono">falResult.{outputKey}</code>
              </p>
            </div>
          </div>
          <div className="bg-status-error/10 border border-status-error/20 rounded p-2 mt-2">
            <p className="text-status-error text-xs font-medium">⚠️ Important: Output Key Format</p>
            <p className="text-th-secondary text-xs mt-1">
              Use only the container path (e.g., "images", "video", "audio"). Do NOT include array indexing like "[0]" or ".url" - the system handles this automatically.
            </p>
          </div>
          
          {/* Real-time Output Key Display */}
          <div className="mt-3 pt-3 border-t border-accent/20">
            <div className="flex items-center justify-between">
              <span className="text-th-secondary text-xs">Current Output Key:</span>
              <code className="text-accent font-mono bg-accent/20 px-2 py-1 rounded text-xs">
                {outputKey || 'not_set'}
              </code>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-th-secondary text-xs">Expected media type:</span>
              <span className="text-accent text-xs font-medium">
                {outputKey?.includes('video') ? 'video' : 
                 outputKey?.includes('audio') ? 'audio' : 'image'}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-th-secondary text-xs">Full access path:</span>
              <code className="text-accent font-mono bg-accent/20 px-1 py-0.5 rounded text-xs">
                falResult.{outputKey}
              </code>
            </div>
          </div>
        </div>
        
        <div className="bg-surface rounded-lg p-4 border border-subtle">
          <pre className="text-th-primary text-xs font-mono overflow-x-auto">
            {JSON.stringify(filteredData, null, 2)}
          </pre>
        </div>
        
        {fieldCount === 0 && (
          <p className="text-th-secondary text-xs italic">
            Fill out the form above to see the JSON payload that will be sent to FAL
          </p>
        )}
      </div>
    </div>
  );
};